/* Mockup lead capture (Custom Card Tools, Phase 1)
 *
 * 1. Records where the visitor came from (first touch per session) on every page.
 * 2. Shows the mobile sticky bar and bottom sheet on eligible pages.
 * 3. On submit, writes attribution tags into contact[tags] and lets Shopify's native
 *    customer form submit normally (creates the customer, triggers the welcome series).
 * 4. When Shopify redirects back with ?customer_posted=true, shows the success state and
 *    publishes a 'mockup_lead_submitted' event for the Meta custom pixel, plus Clarity events.
 */
(function () {
  'use strict';

  if (window.__mlcLoaded) return;
  window.__mlcLoaded = true;

  var ATTR_KEY = 'mlc_attr';
  var PENDING_KEY = 'mlc_pending';
  var DISMISS_KEY = 'mlc_bar_dismissed';
  var CONVERTED_KEY = 'mlc_converted_at';
  var PENDING_MAX_AGE_MS = 15 * 60 * 1000;
  var CONVERTED_HIDE_MS = 30 * 24 * 60 * 60 * 1000;
  var BAR_SCROLL_PX = 250;
  var BAR_DELAY_MS = 4000;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  var FREE_DOMAINS = [
    'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'yahoo.co.uk', 'yahoo.com.au',
    'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'msn.com', 'icloud.com', 'me.com',
    'mac.com', 'aol.com', 'protonmail.com', 'proton.me', 'gmx.com', 'mail.com', 'comcast.net',
    'att.net', 'verizon.net', 'sbcglobal.net', 'bigpond.com', 'optusnet.com.au'
  ];

  /* ---------- storage helpers ---------- */

  function store(kind) {
    try { return window[kind]; } catch (e) { return null; }
  }
  function readJSON(key, kind) {
    var s = store(kind || 'sessionStorage');
    if (!s) return null;
    try { return JSON.parse(s.getItem(key)); } catch (e) { return null; }
  }
  function writeJSON(key, value, kind) {
    var s = store(kind || 'sessionStorage');
    if (!s) return;
    try { s.setItem(key, JSON.stringify(value)); } catch (e) {}
  }
  function removeKey(key, kind) {
    var s = store(kind || 'sessionStorage');
    if (!s) return;
    try { s.removeItem(key); } catch (e) {}
  }

  /* ---------- small utilities ---------- */

  function slug(value, max) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, max || 60);
  }

  function normalizeDomain(value) {
    var v = String(value || '').trim().toLowerCase();
    if (!v) return '';
    v = v.replace(/^[a-z]+:\/\//, '').replace(/^www\./, '').split(/[\/?#\s]/)[0];
    return /^[a-z0-9.-]+\.[a-z]{2,}$/.test(v) ? v : '';
  }

  function device() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
  }

  function clarityEvent(name) {
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (e) {}
  }

  function claritySet(key, value) {
    try { if (typeof window.clarity === 'function') window.clarity('set', key, value); } catch (e) {}
  }

  /* ---------- 1. attribution (first touch per session) ---------- */

  function classify(a) {
    var s = a.source.toLowerCase();
    var m = a.medium.toLowerCase();
    var r = a.referrer.toLowerCase();
    if (a.fbclid || /^(facebook|fb|instagram|ig|meta)$/.test(s)) return 'meta';
    if (a.gclid || (s === 'google' && /(cpc|ppc|paid)/.test(m))) return 'google-ads';
    if (s.indexOf('chatgpt') > -1 || r.indexOf('chatgpt') > -1 || r.indexOf('openai') > -1) return 'chatgpt';
    if (s === 'tiktok' || r.indexOf('tiktok') > -1) return 'tiktok';
    if (m === 'email' || s.indexOf('mailchimp') > -1) return 'email';
    if (/(^|\.)google\./.test(r)) return 'google-organic';
    if (/(^|\.)(bing|duckduckgo|yahoo)\./.test(r)) return 'other-search';
    if (/(^|\.)(facebook|instagram)\./.test(r)) return 'meta-organic';
    if (s) return 'other';
    if (r) return 'referral';
    return 'direct';
  }

  function captureAttribution() {
    if (readJSON(ATTR_KEY)) return;
    var p = new URLSearchParams(window.location.search);
    var a = {
      source: p.get('utm_source') || '',
      medium: p.get('utm_medium') || '',
      campaign: p.get('utm_campaign') || '',
      content: p.get('utm_content') || '',
      fbclid: !!p.get('fbclid'),
      gclid: !!(p.get('gclid') || p.get('gbraid') || p.get('wbraid')),
      referrer: '',
      landing: window.location.pathname
    };
    try {
      if (document.referrer) {
        var ref = new URL(document.referrer);
        if (ref.hostname !== window.location.hostname) a.referrer = ref.hostname;
      }
    } catch (e) {}
    a.channel = classify(a);
    writeJSON(ATTR_KEY, a);
  }

  function buildTags(ctx) {
    var a = readJSON(ATTR_KEY) || { channel: 'direct' };
    var tags = [
      'mockup-lead',
      'lead-' + ctx.device,
      'lead-page-' + ctx.page,
      'lead-src-' + (a.channel || 'direct')
    ];
    if (ctx.product) tags.push('lead-prod-' + slug(ctx.product, 70));
    if (a.campaign) tags.push('lead-camp-' + slug(a.campaign, 60));
    if (a.content) tags.push('lead-ad-' + slug(a.content, 60));
    if (ctx.website) tags.push('lead-web-' + slug(ctx.website, 80));
    return tags.join(', ');
  }

  /* ---------- 2. bar + sheet ---------- */

  var sheetApi = null;

  function recentlyConverted() {
    var t = readJSON(CONVERTED_KEY, 'localStorage');
    return typeof t === 'number' && Date.now() - t < CONVERTED_HIDE_MS;
  }

  function initBar(root) {
    var bar = root.querySelector('[data-mlc-bar-inner]');
    var sheet = root.querySelector('[data-mlc-sheet]');
    var backdrop = root.querySelector('[data-mlc-backdrop]');
    if (!bar || !sheet || !backdrop) return;

    var html = document.documentElement;
    var shown = false;
    var lastFocus = null;

    function showBar() {
      if (shown || readJSON(DISMISS_KEY) || recentlyConverted()) return;
      shown = true;
      bar.hidden = false;
      void bar.offsetWidth; // force layout so the slide-in transition runs
      requestAnimationFrame(function () {
        bar.classList.add('is-visible');
        html.classList.add('mlc-bar-on');
      });
    }

    function hideBar() {
      bar.classList.remove('is-visible');
      html.classList.remove('mlc-bar-on');
      setTimeout(function () { bar.hidden = true; }, 300);
    }

    var timer = setTimeout(showBar, BAR_DELAY_MS);
    function onScroll() {
      if (window.scrollY > BAR_SCROLL_PX) {
        showBar();
        clearTimeout(timer);
        window.removeEventListener('scroll', onScroll);
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    function focusables() {
      return Array.prototype.filter.call(
        sheet.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'),
        function (el) { return !el.disabled && !el.closest('[hidden]') && el.offsetParent !== null && !el.classList.contains('mlc-hp'); }
      );
    }

    function onKeydown(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open(focusTarget) {
      lastFocus = document.activeElement;
      backdrop.hidden = false;
      sheet.hidden = false;
      html.classList.add('mlc-lock');
      void sheet.offsetWidth; // force layout so the slide-up transition runs
      requestAnimationFrame(function () {
        backdrop.classList.add('is-open');
        sheet.classList.add('is-open');
        (focusTarget || sheet).focus({ preventScroll: true });
      });
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      backdrop.classList.remove('is-open');
      sheet.classList.remove('is-open');
      html.classList.remove('mlc-lock');
      document.removeEventListener('keydown', onKeydown);
      setTimeout(function () {
        backdrop.hidden = true;
        sheet.hidden = true;
      }, 320);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
    }

    root.querySelector('[data-mlc-open]').addEventListener('click', function () {
      clarityEvent('mockup_sheet_opened');
      open();
    });
    root.querySelector('[data-mlc-close]').addEventListener('click', close);
    backdrop.addEventListener('click', close);
    root.querySelector('[data-mlc-dismiss]').addEventListener('click', function () {
      writeJSON(DISMISS_KEY, true);
      clarityEvent('mockup_bar_dismissed');
      hideBar();
    });

    sheetApi = { open: open, close: close, hideBar: hideBar, sheet: sheet };
  }

  /* ---------- 3. form ---------- */

  function showError(form, message) {
    var err = form.querySelector('[data-mlc-error]');
    if (!err) return;
    err.textContent = message;
    err.hidden = false;
  }

  function clearError(form) {
    var err = form.querySelector('[data-mlc-error]');
    if (err) { err.textContent = ''; err.hidden = true; }
    var email = form.querySelector('[data-mlc-email]');
    if (email) email.removeAttribute('aria-invalid');
  }

  function resetSubmit(form) {
    var btn = form.querySelector('[data-mlc-submit]');
    if (!btn) return;
    btn.disabled = false;
    btn.textContent = btn.getAttribute('data-label') || btn.textContent;
  }

  function initForm(form) {
    var wrap = form.closest('[data-mlc-root]');
    var email = form.querySelector('[data-mlc-email]');
    var webField = form.querySelector('[data-mlc-website-field]');
    var web = form.querySelector('[data-mlc-website]');
    var product = form.querySelector('[data-mlc-product]');
    var tagsInput = form.querySelector('[data-mlc-tags]');
    var hp = form.querySelector('[data-mlc-hp]');
    var btn = form.querySelector('[data-mlc-submit]');
    if (!email || !tagsInput) return;

    function toggleWebsite() {
      var domain = (email.value.split('@')[1] || '').trim().toLowerCase();
      if (webField) webField.hidden = FREE_DOMAINS.indexOf(domain) === -1;
    }
    email.addEventListener('input', toggleWebsite);
    email.addEventListener('blur', toggleWebsite);

    form.addEventListener('submit', function (e) {
      clearError(form);

      if (hp && hp.value) { e.preventDefault(); return; }

      var value = email.value.trim();
      if (!EMAIL_RE.test(value)) {
        e.preventDefault();
        email.setAttribute('aria-invalid', 'true');
        showError(form, 'Enter a valid email address, like you@company.com.');
        email.focus();
        return;
      }
      email.value = value;

      var ctx = {
        device: device(),
        page: (wrap && wrap.getAttribute('data-mlc-page')) || 'unknown',
        product: product ? product.value : '',
        website: webField && !webField.hidden && web ? normalizeDomain(web.value) : ''
      };
      tagsInput.value = buildTags(ctx);

      var attr = readJSON(ATTR_KEY) || {};
      writeJSON(PENDING_KEY, {
        formId: form.id,
        path: window.location.pathname,
        email: value.toLowerCase(),
        product: ctx.product,
        page: ctx.page,
        device: ctx.device,
        source: attr.channel || 'direct',
        eventId: 'lead_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8),
        ts: Date.now()
      });

      clarityEvent('mockup_lead_attempt');
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
      // No preventDefault: Shopify's native customer form submits and redirects back.
    });
  }

  /* ---------- 4. return from Shopify ---------- */

  function publishLead(pending) {
    var data = {
      email: pending.email,
      product: pending.product,
      page: pending.page,
      device: pending.device,
      source: pending.source,
      event_id: pending.eventId
    };
    var tries = 0;
    (function attempt() {
      var s = window.Shopify;
      if (s && s.analytics && typeof s.analytics.publish === 'function') {
        try { s.analytics.publish('mockup_lead_submitted', data); } catch (e) {}
        return;
      }
      if (++tries < 20) setTimeout(attempt, 250);
    })();
  }

  function cleanUrl() {
    try {
      var u = new URL(window.location.href);
      u.searchParams.delete('customer_posted');
      window.history.replaceState(null, '', u.pathname + u.search);
    } catch (e) {}
  }

  function showSuccess(form, pending) {
    var wrap = form.closest('[data-mlc-root]');
    var success = wrap && wrap.querySelector('[data-mlc-success]');
    if (!success) return;

    form.hidden = true;
    success.hidden = false;
    var emailEl = success.querySelector('[data-mlc-success-email]');
    if (emailEl) emailEl.textContent = pending.email;

    var upload = success.querySelector('[data-mlc-upload]');
    if (upload) {
      try {
        var u = new URL(upload.href);
        if (pending.product) u.searchParams.set('p', pending.product);
        upload.href = u.toString();
      } catch (e) {}
      upload.addEventListener('click', function () { clarityEvent('mockup_upload_clicked'); });
    }

    if (sheetApi && sheetApi.sheet.contains(form)) {
      sheetApi.hideBar();
      sheetApi.open(success);
    } else {
      success.focus({ preventScroll: true });
      wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function handleReturn() {
    var pending = readJSON(PENDING_KEY);
    if (!pending) return;

    if (Date.now() - pending.ts > PENDING_MAX_AGE_MS) { removeKey(PENDING_KEY); return; }

    var params = new URLSearchParams(window.location.search);
    var form = document.getElementById(pending.formId);

    if (params.get('customer_posted') === 'true') {
      removeKey(PENDING_KEY);
      writeJSON(CONVERTED_KEY, Date.now(), 'localStorage');
      if (form) showSuccess(form, pending);
      clarityEvent('mockup_lead_submitted');
      claritySet('mockup_lead_source', pending.source);
      publishLead(pending);
      cleanUrl();
      return;
    }

    // Back on the same page, anchored to our form, without a success flag: Shopify rejected it.
    if (form && window.location.pathname === pending.path && window.location.hash === '#' + pending.formId) {
      removeKey(PENDING_KEY);
      resetSubmit(form);
      showError(form, "That didn't go through. Check your email address and try again.");
      if (sheetApi && sheetApi.sheet.contains(form)) sheetApi.open(form.querySelector('[data-mlc-email]'));
    }
  }

  /* ---------- init ---------- */

  function init() {
    captureAttribution();
    Array.prototype.forEach.call(document.querySelectorAll('form[data-mlc-form]'), initForm);
    var barRoot = document.querySelector('[data-mlc-bar]');
    if (barRoot) initBar(barRoot);
    handleReturn();
  }

  // Re-enable buttons if the visitor comes back via the browser's back button (bfcache).
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    Array.prototype.forEach.call(document.querySelectorAll('form[data-mlc-form]'), resetSubmit);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
